// Pure Fuel Cost Calculator - No duplication, just real-time calculation
// This service calculates fuel costs on-the-fly from fuel_transactions without storing duplicates

import { 
  collection, 
  query, 
  where, 
  getDocs
} from 'firebase/firestore';
import { db } from '../../firebase';
import { calculateVATBreakdown } from '../../utils/vat-calculations';

class FuelCostCalculator {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 2 * 60 * 1000; // 2 minutes cache
  }

  // Calculate fuel costs for a shipment in real-time (no duplication)
  async calculateFuelCostsForShipment(companyId, shipmentId) {
    try {
      const cacheKey = `${companyId}_${shipmentId}`;
      const cached = this.cache.get(cacheKey);
      
      if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
        return cached.data;
      }

      console.log(`💰 Calculating fuel costs for shipment ${shipmentId}...`);

      // Get all fuel transactions for this shipment (real-time query)
      const transactionsQuery = query(
        collection(db, 'fuel_transactions'),
        where('companyId', '==', companyId),
        where('shipment_id', '==', shipmentId)
      );

      const querySnapshot = await getDocs(transactionsQuery);
      const transactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      console.log(`📋 Found ${transactions.length} fuel transactions for shipment ${shipmentId}`);

      // Calculate PetroApp fuel costs from actual transactions
      const petroAppFuelAmount = transactions.reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0);
      const totalLiters = transactions.reduce((sum, tx) => sum + (parseFloat(tx.liters) || 0), 0);

      // Calculate VAT breakdown (assuming fuel includes VAT)
      const petroAppVATBreakdown = calculateVATBreakdown(petroAppFuelAmount, true);

      // Get manual fuel amount from costing (if exists)
      let manualFuelAmount = 0;
      let manualFuelBaseAmount = 0;
      let manualFuelVATAmount = 0;
      let manualFuelIncludesVAT = true;

      try {
        const costingsQuery = query(
          collection(db, 'shipmentCostings'),
          where('companyId', '==', companyId),
          where('shipmentId', '==', shipmentId)
        );

        const costingsSnapshot = await getDocs(costingsQuery);
        if (!costingsSnapshot.empty) {
          const costingData = costingsSnapshot.docs[0].data();
          manualFuelAmount = parseFloat(costingData.manualFuelAmount) || 0;
          manualFuelIncludesVAT = costingData.manualFuelIncludesVAT !== false;
          
          if (manualFuelAmount > 0) {
            const manualVATBreakdown = calculateVATBreakdown(manualFuelAmount, manualFuelIncludesVAT);
            manualFuelBaseAmount = manualVATBreakdown.baseAmount;
            manualFuelVATAmount = manualVATBreakdown.vatAmount;
          }
        }
      } catch (error) {
        console.warn('Could not get manual fuel amount:', error);
      }

      // Calculate combined totals
      const totalFuelAmount = petroAppFuelAmount + manualFuelAmount;
      const totalFuelBaseAmount = petroAppVATBreakdown.baseAmount + manualFuelBaseAmount;
      const totalFuelVATAmount = petroAppVATBreakdown.vatAmount + manualFuelVATAmount;

      // Create comprehensive fuel cost data
      const fuelCostData = {
        // PetroApp fuel (calculated from actual transactions)
        petroAppFuelAmount,
        petroAppFuelBaseAmount: petroAppVATBreakdown.baseAmount,
        petroAppFuelVATAmount: petroAppVATBreakdown.vatAmount,
        petroAppTransactionCount: transactions.length,
        petroAppTotalLiters: totalLiters,
        
        // Manual fuel (from costing if exists)
        manualFuelAmount,
        manualFuelBaseAmount,
        manualFuelVATAmount,
        manualFuelIncludesVAT,
        
        // Combined totals
        totalFuelAmount,
        totalFuelBaseAmount,
        totalFuelVATAmount,
        
        // Transaction details (for display)
        transactions: transactions.map(tx => ({
          id: tx.id,
          invoice_number: tx.invoice_number,
          vehicle_plate: tx.vehicle_plate,
          fill_time: tx.fill_time,
          liters: tx.liters,
          cost: tx.cost,
          station_name: tx.station_name,
          station_location: tx.station_location, // Google Maps link
          fuel_type: tx.fuel_type,
          km_per_liter: tx.km_per_liter,
          liters_per_100km: tx.liters_per_100km,
          governorate: tx.governorate,
          city: tx.city,
          district: tx.district,
          delegate: tx.delegate,
          odometer: tx.odometer,
          formattedFillTime: this.formatDate(tx.fill_time),
          formattedCost: `${parseFloat(tx.cost || 0).toFixed(2)} SAR`,
          formattedLiters: `${parseFloat(tx.liters || 0).toFixed(2)} L`
        })),
        
        // Status information
        hasPetroAppFuel: petroAppFuelAmount > 0,
        hasManualFuel: manualFuelAmount > 0,
        hasAnyFuel: totalFuelAmount > 0,
        
        // Efficiency metrics
        averageCostPerLiter: totalLiters > 0 ? petroAppFuelAmount / totalLiters : 0,
        
        // Metadata
        calculatedAt: new Date(),
        isRealTimeCalculation: true // Flag to indicate this is calculated, not stored
      };

      // Cache the result
      this.cache.set(cacheKey, {
        data: fuelCostData,
        timestamp: Date.now()
      });

      console.log(`✅ Calculated fuel costs for shipment ${shipmentId}: ${totalFuelAmount} SAR (${transactions.length} transactions)`);

      return fuelCostData;

    } catch (error) {
      console.error(`Error calculating fuel costs for shipment ${shipmentId}:`, error);
      throw error;
    }
  }

  // Calculate fuel costs for multiple shipments at once
  async calculateFuelCostsForShipments(companyId, shipmentIds) {
    try {
      const results = new Map();
      
      // Process in parallel for better performance
      const promises = shipmentIds.map(async (shipmentId) => {
        try {
          const fuelCosts = await this.calculateFuelCostsForShipment(companyId, shipmentId);
          results.set(shipmentId, fuelCosts);
        } catch (error) {
          console.error(`Error calculating fuel costs for shipment ${shipmentId}:`, error);
          results.set(shipmentId, {
            totalFuelAmount: 0,
            petroAppFuelAmount: 0,
            manualFuelAmount: 0,
            transactions: [],
            error: error.message
          });
        }
      });

      await Promise.all(promises);
      return results;

    } catch (error) {
      console.error('Error calculating fuel costs for multiple shipments:', error);
      throw error;
    }
  }

  // Get fuel cost summary for a company
  async getFuelCostSummary(companyId) {
    try {
      console.log(`📊 Calculating fuel cost summary for company ${companyId}...`);

      // Get all fuel transactions for the company
      const transactionsQuery = query(
        collection(db, 'fuel_transactions'),
        where('companyId', '==', companyId)
      );

      const querySnapshot = await getDocs(transactionsQuery);
      const allTransactions = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Calculate summary statistics
      const summary = {
        totalTransactions: allTransactions.length,
        assignedTransactions: allTransactions.filter(tx => tx.shipment_id).length,
        unassignedTransactions: allTransactions.filter(tx => !tx.shipment_id).length,
        
        totalFuelCost: allTransactions.reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0),
        assignedFuelCost: allTransactions
          .filter(tx => tx.shipment_id)
          .reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0),
        unassignedFuelCost: allTransactions
          .filter(tx => !tx.shipment_id)
          .reduce((sum, tx) => sum + (parseFloat(tx.cost) || 0), 0),
        
        totalLiters: allTransactions.reduce((sum, tx) => sum + (parseFloat(tx.liters) || 0), 0),
        
        uniqueShipments: new Set(allTransactions.filter(tx => tx.shipment_id).map(tx => tx.shipment_id)).size,
        uniqueVehicles: new Set(allTransactions.map(tx => tx.vehicle_plate)).size,
        
        averageCostPerTransaction: 0,
        averageCostPerLiter: 0,
        assignmentRate: 0
      };

      // Calculate averages and rates
      if (summary.totalTransactions > 0) {
        summary.averageCostPerTransaction = summary.totalFuelCost / summary.totalTransactions;
        summary.assignmentRate = summary.assignedTransactions / summary.totalTransactions;
      }

      if (summary.totalLiters > 0) {
        summary.averageCostPerLiter = summary.totalFuelCost / summary.totalLiters;
      }

      console.log(`✅ Fuel cost summary: ${summary.totalFuelCost} SAR across ${summary.totalTransactions} transactions`);

      return summary;

    } catch (error) {
      console.error('Error calculating fuel cost summary:', error);
      throw error;
    }
  }

  // Helper method to format dates
  formatDate(timestamp) {
    if (!timestamp) return '';
    
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  // Clear cache for a specific shipment
  clearShipmentCache(companyId, shipmentId) {
    const cacheKey = `${companyId}_${shipmentId}`;
    this.cache.delete(cacheKey);
  }

  // Clear all cache
  clearAllCache() {
    this.cache.clear();
  }

  // Force refresh fuel costs for a shipment
  async refreshFuelCosts(companyId, shipmentId) {
    this.clearShipmentCache(companyId, shipmentId);
    return await this.calculateFuelCostsForShipment(companyId, shipmentId);
  }
}

export const fuelCostCalculator = new FuelCostCalculator();